import { z } from 'zod'

// Price schema for variants
export const VariantPriceSchema = z.object({
    id: z.string(),
    amount: z.number().default(0),
    currency: z.string().default('USD'),
    recurring: z.boolean(),
    interval: z.string().nullable(),
    intervalCount: z.number().nullable(),
    default: z.boolean().nullable(),
    billingTiming: z.string().nullable(),
    rebillMode: z.enum(['auth', 'capture']).nullable(),
    rebillStepIntervalMs: z.number().nullable(),
    rebillWithOrder: z.boolean().nullable(),
    currencyOptions: z
        .record(
            z.string(),
            z.object({
                amount: z.number(),
                rate: z.number().optional(),
                lock: z.boolean().optional(),
                lockedBy: z.enum(['merchant', 'sync']).optional(),
                date: z.string().optional(),
            }),
        )
        .nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
})

// Basic variant schema
export const VariantSchema = z.object({
    id: z.string(),
    productId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    sku: z.string().nullable(),
    default: z.boolean(),
    grams: z.number().nullable(),
    imageUrl: z.string().nullable(),
    active: z.boolean(),
    externalVariantId: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    inStock: z.boolean(),
    creditPrice: z.number().nullable().optional(),
})

// Variant with prices
export const VariantWithPricesSchema = VariantSchema.extend({
    prices: z.array(VariantPriceSchema),
})

// Variant with product relation
export const VariantWithProductSchema = VariantSchema.extend({
    product: z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().nullable(),
        active: z.boolean(),
        storeId: z.string(),
        createdAt: z.date(),
        updatedAt: z.date(),
    }),
})

// Variant with all relations
export const VariantWithRelationsSchema = VariantSchema.extend({
    prices: z.array(VariantPriceSchema),
    product: z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().nullable(),
        active: z.boolean(),
        storeId: z.string(),
        accountId: z.string(),
        createdAt: z.date(),
        updatedAt: z.date(),
    }),
})

// Variant details for detail views
export const VariantDetailsSchema = z.object({
    id: z.string(),
    productId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    sku: z.string().nullable(),
    default: z.boolean(),
    grams: z.number().nullable(),
    imageUrl: z.string().nullable(),
    active: z.boolean(),
    externalVariantId: z.string().nullable(),
    prices: z.array(VariantPriceSchema),
    product: z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().nullable(),
        active: z.boolean(),
        storeId: z.string(),
    }),
    createdAt: z.date(),
    updatedAt: z.date(),
})

// List item schema (optimized for lists/tables)
export const VariantListItemSchema = z.object({
    id: z.string(),
    productId: z.string(),
    name: z.string(),
    sku: z.string().nullable(),
    default: z.boolean(),
    active: z.boolean(),
    imageUrl: z.string().nullable(),
    product: z.object({
        id: z.string(),
        name: z.string(),
        storeId: z.string(),
    }),
    createdAt: z.date(),
})

export type VariantPrice = z.infer<typeof VariantPriceSchema>
export type Variant = z.infer<typeof VariantSchema>
export type VariantWithPrices = z.infer<typeof VariantWithPricesSchema>
export type VariantWithProduct = z.infer<typeof VariantWithProductSchema>
export type VariantWithRelations = z.infer<typeof VariantWithRelationsSchema>
export type VariantDetails = z.infer<typeof VariantDetailsSchema>
export type VariantListItem = z.infer<typeof VariantListItemSchema>

// DTO converters
export const VariantDto = (object: unknown) => VariantSchema.parse(object)
export const VariantWithPricesDto = (object: unknown) => VariantWithPricesSchema.parse(object)
export const VariantWithProductDto = (object: unknown) => VariantWithProductSchema.parse(object)
export const VariantWithRelationsDto = (object: unknown) => VariantWithRelationsSchema.parse(object)
export const VariantDetailsDto = (object: unknown) => VariantDetailsSchema.parse(object)
export const VariantListItemDto = (object: unknown) => VariantListItemSchema.parse(object)

